/**
 * Ordinary least squares with several predictors: ŷ = b₀ + b₁x₁ + … + b_kx_k.
 *
 * The fit is a Householder QR (`linalg.ts`), and the standard inference follows from it:
 * s² = SSE / (n − k − 1), Var(b) = s²(XᵀX)⁻¹, t-tests against zero, the overall F test
 * F = (R² / k) / ((1 − R²) / (n − k − 1)), and the usual per-case diagnostics (leverage,
 * standardised residuals, Cook's distance). A predictor that is a linear combination of
 * the others is aliased and reported as such, exactly as R's `lm` does with NA.
 *
 * Checked against R's `lm`, `summary.lm`, `confint`, `AIC`, `BIC`, `hatvalues`,
 * `rstandard`, `cooks.distance` and `car::vif`'s definition.
 */

import { mean, standardDeviation } from "@/lib/stats/descriptive";
import {
  fUpper,
  studentTQuantile,
  studentTTwoTailed,
} from "@/lib/stats/distributions";
import { qrLeastSquares } from "@/lib/tool-kit/stats-regression/linalg";

export interface CoefficientRow {
  name: string;
  estimate: number;
  se: number;
  t: number;
  p: number;
  ciLow: number;
  ciHigh: number;
  /** Standardised coefficient b · sd(x) / sd(y); null for the intercept. */
  beta: number | null;
  /** Variance inflation factor; null for the intercept. */
  vif: number | null;
  aliased: boolean;
}

export interface CaseRow {
  fitted: number;
  residual: number;
  leverage: number;
  standardized: number;
  cooks: number;
}

export interface OlsReport {
  n: number;
  /** Predictors supplied, and how many survived aliasing. */
  predictorCount: number;
  rank: number;
  dfModel: number;
  dfResidual: number;
  sse: number;
  ssr: number;
  sst: number;
  r2: number;
  adjR2: number;
  /** Residual standard error, s. */
  sigma: number;
  f: number;
  pF: number;
  aic: number;
  bic: number;
  /** t* for the requested confidence level. */
  tCritical: number;
  level: number;
  /** Intercept first, then one row per predictor in the order supplied. */
  coefficients: CoefficientRow[];
  cases: CaseRow[];
  /** (XᵀX)⁻¹ in the order intercept, predictors (for prediction intervals). */
  covariance: number[][];
  anyAliased: boolean;
}

function twoSided(t: number, df: number): number {
  if (Number.isNaN(t)) {
    return Number.NaN;
  }
  return Number.isFinite(t) ? studentTTwoTailed(t, df) : 0;
}

/**
 * Variance inflation factors: the diagonal of the inverse correlation matrix of the
 * predictors, VIFⱼ = 1 / (1 − R²ⱼ). Computed from the QR of the standardised columns so
 * that near-collinearity does not wreck the inverse. Infinity for an aliased predictor.
 */
export function varianceInflation(
  predictors: readonly (readonly number[])[]
): number[] {
  const n = predictors[0]?.length ?? 0;
  if (predictors.length < 2 || n < 3) {
    return predictors.map(() => 1);
  }
  const standardised = predictors.map((column) => {
    const m = mean(column);
    const sd = standardDeviation(column);
    return column.map((value) => (sd > 0 ? (value - m) / sd : 0));
  });
  const fit = qrLeastSquares(standardised, new Array<number>(n).fill(0));
  return predictors.map((_, j) =>
    fit.aliased[j] ? Number.POSITIVE_INFINITY : fit.covariance[j][j] * (n - 1)
  );
}

export function olsReport(
  names: readonly string[],
  predictors: readonly (readonly number[])[],
  y: readonly number[],
  level = 0.95
): OlsReport | null {
  const n = y.length;
  const k = predictors.length;
  if (k < 1 || n < k + 2 || predictors.some((column) => column.length !== n)) {
    return null;
  }
  const ybar = mean(y);
  let sst = 0;
  for (const value of y) {
    sst += (value - ybar) ** 2;
  }
  if (sst === 0) {
    return null;
  }
  const ones = new Array<number>(n).fill(1);
  const fit = qrLeastSquares([ones, ...predictors], y);
  const dfResidual = n - fit.rank;
  if (dfResidual < 1) {
    return null;
  }
  const dfModel = fit.rank - 1;
  const s2 = fit.sse / dfResidual;
  const sigma = Math.sqrt(s2);
  const sse = fit.sse;
  const ssr = Math.max(0, sst - sse);
  const r2 = 1 - sse / sst;
  const adjR2 = 1 - ((1 - r2) * (n - 1)) / dfResidual;
  const f = dfModel > 0 ? ssr / dfModel / s2 : Number.NaN;
  const tCritical = studentTQuantile(1 - (1 - level) / 2, dfResidual);
  const vifs = varianceInflation(predictors);
  const sdY = standardDeviation(y);

  const coefficients: CoefficientRow[] = [];
  for (let j = 0; j <= k; j++) {
    const aliased = fit.aliased[j];
    const estimate = fit.coefficients[j];
    const se = aliased ? Number.NaN : Math.sqrt(s2 * fit.covariance[j][j]);
    const t = se > 0 ? estimate / se : Number.NaN;
    coefficients.push({
      name: j === 0 ? "" : names[j - 1],
      estimate,
      se,
      t,
      p: twoSided(t, dfResidual),
      ciLow: estimate - tCritical * se,
      ciHigh: estimate + tCritical * se,
      beta:
        j === 0 || aliased
          ? null
          : (estimate * standardDeviation(predictors[j - 1])) / sdY,
      vif: j === 0 ? null : vifs[j - 1],
      aliased,
    });
  }

  const logLik =
    -(n / 2) * (Math.log(2 * Math.PI) + 1 - Math.log(n) + Math.log(sse));
  const cases: CaseRow[] = fit.fitted.map((fitted, i) => {
    const h = fit.leverage[i];
    const residual = fit.residuals[i];
    const standardized = residual / (sigma * Math.sqrt(1 - h));
    return {
      fitted,
      residual,
      leverage: h,
      standardized,
      cooks: ((standardized * standardized) / fit.rank) * (h / (1 - h)),
    };
  });

  return {
    n,
    predictorCount: k,
    rank: fit.rank,
    dfModel,
    dfResidual,
    sse,
    ssr,
    sst,
    r2,
    adjR2,
    sigma,
    f,
    pF: Number.isFinite(f) ? fUpper(f, dfModel, dfResidual) : Number.NaN,
    aic: -2 * logLik + 2 * (fit.rank + 1),
    bic: -2 * logLik + Math.log(n) * (fit.rank + 1),
    tCritical,
    level,
    coefficients,
    cases,
    covariance: fit.covariance,
    anyAliased: fit.aliased.some(Boolean),
  };
}

export interface Prediction {
  fitted: number;
  /** Interval for the mean response at these predictor values. */
  confidence: [number, number];
  /** Interval for a single new observation. */
  prediction: [number, number];
  /** Leverage of the new point; large values flag extrapolation. */
  leverage: number;
}

/** ŷ at a new point (aliased predictors are ignored), with both intervals. */
export function predictAt(
  report: OlsReport,
  values: readonly number[]
): Prediction {
  const x = [1, ...values];
  let fitted = 0;
  let quad = 0;
  for (let a = 0; a < x.length; a++) {
    if (report.coefficients[a].aliased) {
      continue;
    }
    fitted += report.coefficients[a].estimate * x[a];
    for (let b = 0; b < x.length; b++) {
      if (!report.coefficients[b].aliased) {
        quad += x[a] * report.covariance[a][b] * x[b];
      }
    }
  }
  const s2 = report.sigma ** 2;
  const seMean = Math.sqrt(s2 * quad);
  const sePred = Math.sqrt(s2 * (1 + quad));
  const t = report.tCritical;
  return {
    fitted,
    confidence: [fitted - t * seMean, fitted + t * seMean],
    prediction: [fitted - t * sePred, fitted + t * sePred],
    leverage: quad,
  };
}
